import { END_POINTS } from "../constants/urls";
import emptySplitApi from "../utils/rtk";

interface CommonResponseType {
  statusCode: number;
  message: string;
}

export interface AddNotificationBody {
  title: string;
  content: string;
  notificationType: number;
  isBroadcast: boolean;
  userList: string[];
  isMultiReceivers: boolean;
  scheduleType: number;
  scheduleAt: string; // ISO string
}

export interface NotificationItem {
  _id?: string;
  id?: string;
  title?: string;
  message?: string; // API uses "message" in list response
  content?: string; // kept for compatibility with other endpoints
  notificationType?: number;
  type?: number; // some endpoints may return `type`
  isBroadcast?: boolean;
  isMultiReceivers?: boolean;
  scheduleType?: number;
  scheduleAt?: string;
  receiversCount?: number;
  createdAt?: string;
  updatedAt?: string;
  broadcastReciever?: { username: string }[];
  previewText?: string;
}

type GetNotificationResponse = CommonResponseType & {
  data: {
    notificationData: NotificationItem[];
    totalNotification: number;
  };
};

export const NotificationApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    addNotification: builder.mutation<
      CommonResponseType & { data?: NotificationItem },
      AddNotificationBody
    >({
      query: (body) => ({
        url: END_POINTS.notification,
        method: "POST",
        body,
      }),
      invalidatesTags: ["NOTIFICATIONS"],
    }),
    updateNotification: builder.mutation<
      CommonResponseType & { data?: NotificationItem },
      { body: Partial<AddNotificationBody> & { id?: string } }
    >({
      query: ({ body }) => ({
        url: `${END_POINTS.notification}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["NOTIFICATIONS"],
    }),
    getNotification: builder.query<
      GetNotificationResponse,
      { page: number; limit: number; search?: string; type?: number }
    >({
      query: ({ page, limit, search, type }) => ({
        url: `${END_POINTS.getNotification}?page=${page}&limit=${limit}${
          search ? `&search=${search}` : ""
        }${type ? `&type=${type}` : ""}`,
        method: "GET",
      }),
      providesTags: ["NOTIFICATIONS"],
    }),
    getNotificationById: builder.query<
      CommonResponseType & { data: NotificationItem },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `${END_POINTS.getNotificationDetails}/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, { id }) => [{ type: "NOTIFICATIONS", id }],
    }),
    deleteNotification: builder.mutation<
      CommonResponseType & { data?: any },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `${END_POINTS.notification}/${id}`,
        method: "DELETE",
        body: {},
      }),
      invalidatesTags: ["NOTIFICATIONS"],
    }),
  }),
});

export const {
  useAddNotificationMutation,
  useUpdateNotificationMutation,
  useDeleteNotificationMutation,
  useLazyGetNotificationQuery,
  useGetNotificationByIdQuery,
  useLazyGetNotificationByIdQuery,
} = NotificationApi;
